import { Button, Text, useNotifications } from "@ilc-technology/luik";
import "../../App.scss";
import parse from "html-react-parser";
import { useNavigate } from "react-router-dom";
import { forwardRef } from "react";
import { Quote, QuotePaymentStatus } from "../../Common/Types";
import { useSessionContext } from "../../contexts/SessionContext/SessionContext";
import { QuoteDraft, useQuoteDetailsContext } from "../../contexts/QuoteDetailsContext";
import { useQuoteContext } from "../../contexts/QuoteContext/QuoteContext";
import { useDatasources } from "../../contexts/StoryblokContext/StoryblokContext";
import { LabelKey } from "../../Common/StoryblokTypes";
import {
  CustomTrackingEvent,
  StandardTrackingEvent,
  trackEvent,
  QuoteEventType,
} from "../../Common/services/Analytics";
import { logError } from "../../Common/services/ErrorService";
import { generatePriceQuoteEventItems } from "../../Common/services/AnalyticsMappings";
import { TRUE } from "../../Common/Constants";
import { FormattedMoney } from "../../Common/Helpers/MoneyHelper";
import Enrich from "../../Common/services/TextEnricher";
import { formatDate } from "../../Common/Helpers/DateHelper";
import { formatQuoteTotalAsShownInQuoteHeader } from "../../Common/services/PaymentsService";

interface BookNowProps {
  isSaveInProgress: boolean;
  setSaveInProgress: (value: boolean) => void;
  quote: Quote;
  quoteDraft: QuoteDraft;
}

const BookNow = forwardRef<HTMLDivElement, BookNowProps>(
  ({ isSaveInProgress, setSaveInProgress = () => {}, quote, quoteDraft }, ref) => {
    const { language } = useSessionContext();
    const { saveQuote } = useQuoteDetailsContext();
    const { sendQuoteEvent } = useQuoteContext();
    const { labels, featureSettings } = useDatasources();
    const navigate = useNavigate();
    const notifications = useNotifications();
    const isPaymentEnabled = featureSettings.isPaymentEnabled === TRUE;
    const isReservationEnabled = featureSettings.isReservationEnabled === TRUE;

    const enricherContext = {
      currentQuote: quoteDraft.quoteDraft,
    };
    const reservationPrice = quoteDraft.quoteDraft.reservationPrice;
    const buttonLabelKey = quoteDraft.isQuoteDraftEdited
      ? LabelKey.saveAsNew
      : quote.status === QuotePaymentStatus.Unreserved
        ? LabelKey.bookNow
        : LabelKey.payNow;
    const quoteStatus = quote?.status;
    const totalPrice = quoteDraft.quoteDraft.quoteData.prices.totalPrice;
    const displayedQuoteTotal =
      quoteStatus === QuotePaymentStatus.Paid
        ? formatQuoteTotalAsShownInQuoteHeader(totalPrice, quote.payments)
        : FormattedMoney(totalPrice);
    const handleButtonPress = async () => {
      sendQuoteEvent(quote.id, QuoteEventType.ClickedReservationButton);
      if (quoteDraft.isQuoteDraftEdited) {
        setSaveInProgress(true);
        const result = await saveQuote(quote);
        setSaveInProgress(false);
        if (result.isSuccessful) {
          trackEvent(quote.opportunityUuid, CustomTrackingEvent.SaveNewQuote);
          notifications.addSuccessNotification({
            title: labels[LabelKey.success],
            description: labels[LabelKey.quoteSaved],
          });
          navigate(`/quote/${result?.quote!.id}`);
        } else {
          logError(result.error);
          notifications.addErrorNotification({
            title: labels[LabelKey.error],
            description: labels[LabelKey.saveFailed],
          });
        }
      } else {
        trackEvent(quote.opportunityUuid, StandardTrackingEvent.BeginCheckout, {
          value: quote.prices.totalPrice.amount,
          currency: quote.prices.totalPrice.currency,
          items: generatePriceQuoteEventItems(quote),
        });
        navigate("/payment/" + quote.id);
      }
    };

    return (
      <div
        id="bookNow"
        ref={ref}
        className="z-10 mb-12 flex  w-full justify-center rounded-lg border-t border-gray-85 bg-white font-normal md:mb-0"
      >
        <div className="flex w-full max-w-content-lg flex-col items-center px-6 py-8 xl:max-w-content-xl">
          <Text variant="heading-5-bold" className="mb-0 text-center">
            {labels[LabelKey.bookNowLabel]}
          </Text>
          <div className="flex w-full max-w-content-lg flex-col items-center gap-4 xl:max-w-content-xl">
            {reservationPrice &&
              (quoteStatus === QuotePaymentStatus.Paid ? (
                <Text // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                  variant=""
                  className="text-center text-label-sm-bold sm:text-label-lg-bold"
                  data-testid="book-now-total"
                >{`${labels[LabelKey.total]}: ${displayedQuoteTotal}`}</Text>
              ) : (
                <div className="flex flex-col items-center gap-2 text-center">
                  <Text // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                    variant=""
                    className="text-label-sm-bold sm:text-label-lg-bold"
                    data-testid="book-now-total"
                  >{`${labels[LabelKey.total]}: ${displayedQuoteTotal}`}</Text>
                  <Text // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                    variant=""
                    className="text-gray-45 text-label-xs"
                  >
                    {Enrich(labels[LabelKey.quoteValidUntil], {
                      quoteValidToDate: formatDate(quote.validUpTo, language, true),
                    })}
                  </Text>
                </div>
              ))}
            {(isPaymentEnabled || isReservationEnabled) &&
              reservationPrice &&
              quoteStatus !== QuotePaymentStatus.Paid && (
                <div className="flex w-full flex-col items-center gap-0">
                  <div className="flex w-full flex-col items-center">
                    <Button
                      size="base"
                      intent="primary-black"
                      isDisabled={isSaveInProgress}
                      isLoading={isSaveInProgress}
                      onPress={handleButtonPress}
                      className="w-full min-w-full"
                    >
                      {labels[buttonLabelKey]}
                    </Button>
                    <div className="mt-2 text-center">
                      <Text // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                        variant=""
                        className="text-label-xs sm:text-label-sm"
                      >
                        {parse(
                          Enrich(
                            labels[LabelKey.reserveFor]?.replace(
                              /(\{quoteReservationPrice\})/,
                              '<span class="font-bold text-[15px] leading-6 md:text-lg">{quoteReservationPrice}</span>'
                            ),
                            enricherContext
                          )
                        )}
                      </Text>
                      <Text // @ts-expect-error We need to pass something to variant otherwise it won't work on mobile
                        variant=""
                        className="text-label-xs sm:text-label-sm"
                      >
                        {labels[LabelKey.orPayLater]}
                      </Text>
                    </div>
                  </div>
                </div>
              )}
          </div>
        </div>
      </div>
    );
  }
);

export default BookNow;
